import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { EnvelopeSimpleIcon, TrashIcon, PaperPlaneTiltIcon } from '@phosphor-icons/react';

import {
  useAdminBetaInvitations,
  createBetaInvitation,
  revokeBetaInvitation,
  resendBetaInvitationEmail,
  type BetaInvitation,
  type InvitationStatusFilter,
} from '@/api-models/admin/beta-invitations';
import { formatDate } from '@/features/admin/format';
import { Pagination } from '@/ui/Pagination';
import { SpinnerBlock } from '@/ui/Spinner';
import { FilterPills } from '@/ui/FilterPills';
import { Button } from '@/ui/button/Button';
import { Badge } from '@/ui/Badge';
import { Card } from '@/ui/Card';
import { Input } from '@/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';

const PAGE_SIZE = 25;

const STATUS_PILLS: { label: string; mode: InvitationStatusFilter }[] = [
  { label: 'All', mode: 'all' },
  { label: 'Pending', mode: 'pending' },
  { label: 'Accepted', mode: 'accepted' },
];

interface InvitationRowProps {
  invitation: BetaInvitation;
  busy: boolean;
  onResend: (id: string) => Promise<void>;
  onRevoke: (id: string) => Promise<void>;
}

function InvitationRow({ invitation, busy, onResend, onRevoke }: InvitationRowProps) {
  const isAccepted = invitation.status === 'accepted';

  return (
    <TableRow>
      <TableCell>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-fg">{invitation.name || invitation.email}</p>
          {invitation.name && <p className="truncate text-2xs text-fg-muted">{invitation.email}</p>}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={isAccepted ? 'success' : 'warning'}>{isAccepted ? 'Accepted' : 'Pending'}</Badge>
      </TableCell>
      <TableCell className="whitespace-nowrap text-2xs text-fg-muted tabular-nums">
        {formatDate(invitation.created_at)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-2xs text-fg-muted tabular-nums">
        {formatDate(invitation.accepted_at)}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          {!isAccepted && (
            <>
              <Button
                size={7}
                variant="ghost"
                onClick={() => onResend(invitation.id)}
                disabled={busy}
                iconLeft={<PaperPlaneTiltIcon size={12} />}
              >
                Resend
              </Button>
              <Button
                size={7}
                variant="ghost"
                onClick={() => onRevoke(invitation.id)}
                disabled={busy}
                iconLeft={<TrashIcon size={12} />}
              >
                Revoke
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function AdminInvitationsPage() {
  const [statusFilter, setStatusFilter] = useState<InvitationStatusFilter>('all');
  const [page, setPage] = useState(1);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const query = useMemo(() => ({ status: statusFilter, page, pageSize: PAGE_SIZE }), [statusFilter, page]);
  const { invitations, total, mutate, isLoading } = useAdminBetaInvitations(query);

  function handleFilterChange(mode: InvitationStatusFilter) {
    setStatusFilter(mode);
    setPage(1);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmail.trim() || creating) return;
    setCreating(true);
    try {
      await createBetaInvitation(newEmail, newName);
      setNewEmail('');
      setNewName('');
      await mutate();
      toast.success('Invitation sent');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setCreating(false);
    }
  }

  async function handleResend(id: string) {
    setBusyId(id);
    try {
      await resendBetaInvitationEmail(id);
      await mutate();
      toast.success('Welcome email resent');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resend email');
    } finally {
      setBusyId(null);
    }
  }

  async function handleRevoke(id: string) {
    if (!window.confirm('Revoke this invitation? The email will no longer be able to sign up.')) {
      return;
    }
    setBusyId(id);
    try {
      await revokeBetaInvitation(id);
      await mutate();
      toast.success('Invitation revoked');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke invitation');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <header>
        <h1 className="page-title">Beta invitations</h1>
        <p className="page-subtitle">
          Invite people into the closed beta. They&apos;ll receive a welcome email and can sign in with the GitHub
          account matching their invited email.
        </p>
      </header>

      <Card padding="sm" radius="2xl">
        <form onSubmit={handleCreate} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="section-header mb-1 block">Email</label>
            <Input
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="person@example.com"
            />
          </div>
          <div className="flex-1">
            <label className="section-header mb-1 block">Name (optional)</label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Their name" />
          </div>
          <Button
            type="submit"
            variant="primary"
            size={9}
            disabled={creating}
            isLoading={creating}
            iconLeft={<EnvelopeSimpleIcon size={14} />}
          >
            Send invite
          </Button>
        </form>
      </Card>

      <div className="flex flex-col gap-4">
        <FilterPills pills={STATUS_PILLS} activeMode={statusFilter} onChange={handleFilterChange} />

        <Card padding="none" radius="2xl" className="overflow-hidden">
          {isLoading && invitations.length === 0 ? (
            <div className="py-12">
              <SpinnerBlock message="Loading invitations…" />
            </div>
          ) : invitations.length === 0 ? (
            <p className="p-8 text-center text-xs text-fg-muted">No invitations match this filter.</p>
          ) : (
            <Table>
              <TableHead>
                <tr>
                  <TableHeaderCell>Invitee</TableHeaderCell>
                  <TableHeaderCell className="w-30">Status</TableHeaderCell>
                  <TableHeaderCell className="w-36">Sent</TableHeaderCell>
                  <TableHeaderCell className="w-36">Accepted</TableHeaderCell>
                  <TableHeaderCell className="w-50 text-right">Actions</TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                {invitations.map((inv) => (
                  <InvitationRow
                    key={inv.id}
                    invitation={inv}
                    busy={busyId === inv.id}
                    onResend={handleResend}
                    onRevoke={handleRevoke}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
      </div>
    </>
  );
}
